import axios from "axios";

import store from "store/index";
import { setAPIKey, setMLPublicApiKey, setIsFetchManagement } from "slices";
import { fetchPropertyData } from "utils";

import { reportApiError, endpointOf } from "./reportApiError";
import { startApiSpan, endApiSpan } from "./apiTiming";

let isRefreshing = false;
let failedQueue = [];
const MAX_RETRY_ATTEMPTS = 2; // 1 original + 1 retry

// Request tracking for cancellation
const activeRequests = new Map();
let requestIdCounter = 0;

export const isManualCancellation = (error) => {
  if (error?.isManualCancellation === true) {
    return true;
  }

  if (error?.config?.signal?.reason?.isManualCancellation === true) {
    return true;
  }

  return false;
};

export const cancelAllRequests = () => {
  activeRequests.forEach((controller) => {
    try {
      controller.abort({ isManualCancellation: true, reason: "chat_restart" });
    } catch (err) {
      console.warn("Failed to abort request:", err);
    }
  });

  activeRequests.clear();

  // Clear retry queue
  failedQueue.forEach(({ reject }) => {
    const error = new Error("Request canceled during chat restart");
    error.isManualCancellation = true;
    reject(error);
  });

  failedQueue = [];
  isRefreshing = false;
};

// Custom transformResponse: explicitly throw on JSON parse failure so that malformed
// JSON bodies with a 200 status route to .catch() instead of silently arriving in
// .then() as a raw string (Axios default behaviour swallows the SyntaxError).
const transformResponse = [
  (data, headers) => {
    const contentType = headers?.["content-type"] || "";

    if (typeof data === "string" && contentType.includes("application/json")) {
      try {
        return JSON.parse(data);
      } catch (e) {
        const err = new SyntaxError(`Malformed JSON in server response: ${e.message}`);
        err.isJsonParseError = true;
        throw err;
      }
    }

    return data;
  },
];

// Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 45000,
  transformResponse,
});

// Check if a request URL targets an ML microservice endpoint
const isMLRequest = (config) => {
  try {
    const mlBaseUrl = process.env.REACT_APP_WS_BASE_URL;

    if (!mlBaseUrl) {
      return false;
    }

    const mlOrigin = new URL(mlBaseUrl).origin;
    const requestUrl = config.url?.startsWith("http") ? config.url : new URL(config.url, axiosInstance.defaults.baseURL).href;

    return requestUrl.startsWith(mlOrigin);
  } catch {
    return false;
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    if (config?.cancellable === true) {
      const requestId = `req_${++requestIdCounter}_${Date.now()}`;

      // Create AbortController
      const controller = new AbortController();
      activeRequests.set(requestId, controller);

      // Attach signal to request
      config.signal = controller.signal;
      config._requestId = requestId;
    }

    const apiKey = store.getState().property.api_key;

    if (config?.data) {
      let payload;
      try {
        payload = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
      } catch (err) {
        console.warn("Invalid JSON in config.data", err);

        return config;
      }

      if (payload && typeof payload === "object") {
        if ("graphql_api_key" in payload) {
          payload.graphql_api_key = apiKey;
        } else if ("api_key" in payload) {
          payload.api_key = apiKey;
        }

        config.data = typeof config.data === "string" ? JSON.stringify(payload) : payload;
      } else {
        return Promise.reject("Invalid payload format.");
      }
    }

    const updatedUrl = new URL(config?.url, axiosInstance.defaults.baseURL);

    if (updatedUrl?.searchParams.has("api_key")) {
      updatedUrl.searchParams.set("api_key", apiKey);
      config.url = updatedUrl.toString();
    }

    // Attach x-api-key header for ML endpoints (STG/PROD only)
    if (process.env.REACT_APP_ML_AUTH_ENABLED === "true" && isMLRequest(config)) {
      const mlApiKey = store.getState().property.mlApiKey;

      if (mlApiKey) {
        config.headers = config.headers || {};
        config.headers["x-api-key"] = mlApiKey;
      }
    }

    // Start a timing span for this request (ended in the response interceptors). Stored on the
    // per-request config so overlapping requests never collide. `updatedUrl` is the resolved
    // absolute URL; endpointOf strips the query so no api_key/PII reaches the span name.
    config.metadata = {
      ...(config.metadata || {}),
      span: startApiSpan({
        client: isMLRequest(config) ? "ml" : "rest",
        method: (config.method || "get").toUpperCase(),
        endpoint: endpointOf(updatedUrl.href),
      }),
    };

    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh token logic
const refreshToken = async (config) => {
  try {
    // Fetch the property data to get the API key.
    // For ML 401s, pass force_refresh so BE fetches the latest key from Secret Manager.
    const propertyId = store.getState().property.property_id;
    const mlRequest = process.env.REACT_APP_ML_AUTH_ENABLED === "true" && isMLRequest(config);

    let res;

    try {
      res = await fetchPropertyData(propertyId, mlRequest);
    } catch (err) {
      // Token refresh couldn't reach the backend -> the session's API access is broken
      // (queued requests will all be rejected). fetchPropertyData runs on the un-intercepted
      // baseAxios, so a network-level failure is invisible to httpClientIntegration -> capture
      // it here. An HTTP-status failure on the token endpoint is already caught by Layer 1, so
      // guard on `!err.response` to avoid a duplicate. The request replay below is NOT wrapped
      // (it goes through the interceptor, which handles its errors).
      if (!err.response) {
        reportApiError(err, {
          client: "rest",
          kind: "token_refresh",
          endpoint: endpointOf(`${process.env.REACT_APP_API_BASE_URL}/properties/api_token`),
        });
      }

      throw err;
    }

    const { api_key, ml_api_key } = res.data;

    store.dispatch(setAPIKey(api_key));

    if (ml_api_key) {
      store.dispatch(setMLPublicApiKey(ml_api_key));
    }

    // ===== 1. Update data (payload) if it exists =====
    if (config?.data) {
      let payload;
      try {
        payload = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
      } catch (err) {
        console.warn("Invalid JSON in config.data", err);

        return config;
      }

      if (payload && typeof payload === "object") {
        if ("graphql_api_key" in payload) {
          payload.graphql_api_key = api_key;
        } else if ("api_key" in payload) {
          payload.api_key = api_key;
        }

        config.data = typeof config.data === "string" ? JSON.stringify(payload) : payload;
      } else {
        return Promise.reject("Invalid payload format.");
      }
    }

    // ===== 2. Only update api_key in URL if it exists =====
    const updatedUrl = new URL(config?.url, axiosInstance.defaults.baseURL);

    if (updatedUrl?.searchParams.has("api_key")) {
      updatedUrl.searchParams.set("api_key", api_key);
      config.url = updatedUrl.toString();
    }

    // ===== 3. Update x-api-key header for ML requests (STG/PROD only) =====
    if (mlRequest && ml_api_key) {
      config.headers = config.headers || {};
      config.headers["x-api-key"] = ml_api_key;
    }

    // Send the request using the updated config
    return await axiosInstance(config);
  } catch (err) {
    return Promise.reject(err);
  }
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // End this request's timing span with its status.
    endApiSpan(response.config?.metadata?.span, { status: response.status });

    // Clean up completed request
    if (response.config._requestId) {
      activeRequests.delete(response.config._requestId);
    }

    return response;
  },
  async (error) => {
    const { response, config } = error;

    // End this attempt's timing span (covers cancellation, HTTP-status, and the 401 path;
    // a 401 retry re-runs the request interceptor and gets a fresh span). A user-initiated
    // cancellation (chat restart) is not a real failure, so don't flag it errored — otherwise
    // it would inflate Sentry's failure-rate for this cancel-heavy widget.
    const isCanceled = error.code === "ERR_CANCELED" || error.name === "CanceledError";

    endApiSpan(config?.metadata?.span, { status: response?.status, errored: !isCanceled });

    if (isCanceled) {
      if (config?._requestId) {
        activeRequests.delete(config._requestId);
      }

      return Promise.reject(error);
    }

    // Clean up failed request
    if (config?._requestId) {
      activeRequests.delete(config._requestId);
    }

    // ML 403 = forbidden (not a stale key) — refreshing won't help, reject immediately
    if (response?.status === 403 && process.env.REACT_APP_ML_AUTH_ENABLED === "true" && isMLRequest(config)) {
      return Promise.reject(error);
    }

    if (response && (response.status === 401 || response.status === 403)) {
      // Increment retry count
      config._retryCount = (config._retryCount || 0) + 1;

      // Stop retrying after max attempts
      if (config._retryCount >= MAX_RETRY_ATTEMPTS) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config });
        });
      }

      isRefreshing = true;
      store.dispatch(setIsFetchManagement(false));

      try {
        const result = await refreshToken(config);

        failedQueue.forEach(({ resolve, reject, config: queuedConfig }) => {
          axiosInstance(queuedConfig).then(resolve).catch(reject);
        });

        failedQueue = [];

        return result;
      } catch (err) {
        failedQueue.forEach(({ reject }) => reject(err));
        failedQueue = [];

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
        store.dispatch(setIsFetchManagement(true));
      }
    }

    // Capture ONLY the gaps httpClientIntegration cannot see: network/timeout (no HTTP
    // response) and malformed-JSON-on-200. Any error.response with a status is an
    // HTTP-status failure already captured by the integration -> skip to avoid duplicates.
    if (!error.response || error.isJsonParseError) {
      const kind = error.isJsonParseError ? "malformed_json" : error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" ? "timeout" : "network";

      reportApiError(error, {
        client: isMLRequest(config || {}) ? "ml" : "rest",
        kind,
        endpoint: endpointOf(config?.url),
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
